<template>
  <div class="facilities-filter">
    <div class="facilities-filter-buttons">
      <button class="facilities-filter__button" @click="openSearch">
        <span class="facilities-filter__button-icon">
          <BaseIcon icon="search" />
        </span>
        <span>{{ $t("Search") }}</span>
      </button>
      <button class="facilities-filter__button" @click="openFilter">
        <span>{{ $t("Filter") }}</span>
      </button>
    </div>

    <div class="facilities-filter-bar">
      <BaseInput
        v-model="searchInput"
        no-error
        clearable
        icon="search"
        :inner-placeholder="preparedPlaceholderSearchInput"
        @focus="onSearchFocus"
      />
      <BaseSelect
        :options="availableLevelsGetter"
        :model-value="level"
        styled
        arrow
        full-height
        radio
        @change="onChangeLevel"
      >
        <template #value>
          {{ level.value }} {{ $t("membership") }}
        </template>
        <template #option="levelsOptionalList">
          {{ levelsOptionalList.option.value }}
        </template>
      </BaseSelect>
      <BaseSelect
        :options="citiesOptionalList"
        :is-disabled-all-options="isCityButtonClicked"
        :model-value="city"
        styled
        arrow
        radio
        @change="onChangeCity"
      >
        <template #value>
          {{ activeCity.value }}
        </template>
        <template #option="citiesOptionalList">
          {{ citiesOptionalList.option.value }}
        </template>
      </BaseSelect>
      <BaseSelect
        :options="filteredActivities"
        :model-value="activities"
        styled
        arrow
        multiple
        checkbox
        :is-tooltip="true"
        @change="setMapFilterActivities"
        searchable
        :clear-search="resetSearchActivities"
      >
        <template #value>
          <template v-if="activities.length">
            <span
              v-for="(activity, index) in activities"
              :key="`activity-value=${activity.id}`"
            >
              {{ index === 0 ? "" : "," }} {{ activity.value }}
            </span>
          </template>
          <span v-else>{{ $t("Activity") }}</span>
        </template>
        <template #first-option>
          <div class="select-option" @click="resetMapFilterActivities">
            {{ $t("Reset all") }}
          </div>
        </template>
        <template #search>
          <BaseInput
            v-model="searchActivities"
            no-error
            clearable
            class="select-search-option"
            icon="search"
            :inner-placeholder="$t('Search')"
          />
        </template>
        <template #option="activitiesOptionalList">
          {{ activitiesOptionalList.option.value }}
        </template>
      </BaseSelect>
      <BaseSelect
        :options="tagsOptionalList"
        :model-value="tags"
        styled
        arrow
        multiple
        checkbox
        :is-tooltip="true"
        @change="setMapFilterTags"
      >
        <template #value>
          <template v-if="tags.length">
            <span
              v-for="(tag, index) in tags"
              :key="`activity-value=${tag.id}`"
            >
              {{ index === 0 ? "" : "," }} {{ tag.value }}
            </span>
          </template>
          <span v-else>{{ $t("More") }}</span>
        </template>
        <template #first-option>
          <div class="select-option" @click="resetMapFilterTags">
            {{ $t("Reset all") }}
          </div>
        </template>
        <template #option="tagsOptionalList">
          {{ tagsOptionalList.option.value }}
        </template>
      </BaseSelect>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, PropType } from "vue";

import { useMapFilterStore } from "~/stores/mapFilter";
import { useCountriesStore } from "~/stores/countries";
import { IMapFiltersResponse } from "~/types/map.interface";
import BaseInput from "~/components/input/BaseInput.vue";
import BaseIcon from "~/components/icons/BaseIcon.vue";
import BaseSelect, { type BaseSelectOption } from "~/components/input/BaseSelect.vue";
import { mapSubscriptionModelToLevels } from "~/constants";

const props = defineProps({
  filtersList: {
    type: Object as PropType<IMapFiltersResponse>,
  },
  isCityButtonClicked: {
    type: Boolean as PropType<boolean>,
  },
  isLoadingRequest: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  isShowSearchModal: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
}); 
const emit = defineEmits(["changeCity", "changeLevel", "openSearchModal", "openFilterModal"]);

const mapFilterStore = useMapFilterStore();
const { currentCountry } = storeToRefs(useCountriesStore());

const {
  activities,
  tags,
  search: searchInput,
  searchActivities,
  city,
  availableLevelsGetter,
  subscriptionModel,
  selectedLevel,
} = storeToRefs(mapFilterStore);

const {
  setMapFilterActivities,
  setMapFilterTags,
  resetMapFilterTags,
  resetMapFilterActivities,
} = mapFilterStore;
const { t } = useI18n();

const level = computed(() => selectedLevel.value[mapSubscriptionModelToLevels[subscriptionModel.value]]);

const activitiesOptionalList = computed(() => {
  return props.filtersList?.activities;
});

const tagsOptionalList = computed(() => {
  return props.filtersList?.tags;
});

const citiesOptionalList = computed(() => {
  return props.filtersList?.cities;
});

const activeCity = computed(() => {
  return props.filtersList?.cities.find((filterCity) => filterCity.id === city.value?.id) ?? city.value;
});

const filteredActivities = computed(() => {
  const searchValue = searchActivities.value.toLowerCase();

  if (!activitiesOptionalList.value || !searchValue) {
    return activitiesOptionalList.value || [];
  }

  const searchTerms = searchValue.split(" ");

  const isSearchTermMatched = ({ value }: BaseSelectOption): boolean => {
    const itemValue = value.toLowerCase();
    return searchTerms.every(term => itemValue.includes(term));
  };

  if (!activitiesOptionalList.value) {
    return [];
  }

  const proxyActivitiesList: BaseSelectOption[] = activitiesOptionalList.value;

  return proxyActivitiesList.filter(isSearchTermMatched);
});

const preparedPlaceholderSearchInput = computed(() => {
  return city.value?.value
    ? t("Search by {location}", { location: city.value.value })
    : t("Search in {country}", { country: currentCountry.value.value });
});

function openFilter() {
  emit("openFilterModal");
}
function openSearch() {
  emit("openSearchModal");
}

const onSearchFocus = () => openSearch();

function onChangeLevel(value: BaseSelectOption) {
  emit("changeLevel", value);
}

function onChangeCity(value: BaseSelectOption) {
  emit("changeCity", value);
}

const resetSearchActivities = () => {
  searchActivities.value = "";
};
</script>
